import React, { createContext, useContext, useState, useEffect } from "react";
import { IntlProvider } from "react-intl";

import en from "@/locales/en.json";
import ar from "@/locales/ar.json";

type LanguageContextType = {
  locale: "en" | "ar";
  isRTL: boolean;
  setLocale: (lang: "en" | "ar") => void;
  setLanguage: (lang: "en" | "ar") => void;
  t: (key: string) => string;
  language: "en" | "ar";
};

type RawIntlMessages = Record<
  string,
  {
    defaultMessage?: string;
    description?: string;
  }
>;

const LanguageContext = createContext<LanguageContextType | null>(null);

const normalizeMessages = (msgs: RawIntlMessages): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const key in msgs) {
    result[key] = msgs[key]?.defaultMessage ?? key;
  }
  return result;
};

const messages: Record<"en" | "ar", Record<string, string>> = {
  en: normalizeMessages(en as RawIntlMessages),
  ar: normalizeMessages(ar as RawIntlMessages),
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize locale from localStorage if available, fallback to "en"
  const [locale, setLocaleState] = useState<"en" | "ar">(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem("app_locale");
    return stored === "ar" || stored === "en" ? stored : "en";
  });

  const setLocale = (lang: "en" | "ar") => {
    setLocaleState(lang);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("app_locale", lang);
    }
  };

  const isRTL = locale === "ar";

  const t = (key: string) => messages[locale][key] ?? key;

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = locale;
  }, [isRTL, locale]);

  return (
    <LanguageContext.Provider
      value={{ locale, isRTL, setLocale, setLanguage: setLocale, t, language: locale }}
    >
      <IntlProvider locale={locale} messages={messages[locale]} defaultLocale="en" key={locale}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
};
